import { useEffect, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const emptyForm = { name: "", start_time: "09:00", end_time: "18:00", weekly_offs: [5, 6] };

export default function ShiftsManager() {
  const [shifts, setShifts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/shifts").then((r) => setShifts(r.data));
  useEffect(() => { load(); }, []);

  const toggleDay = (d) => {
    const has = form.weekly_offs.includes(d);
    setForm({
      ...form,
      weekly_offs: has ? form.weekly_offs.filter((x) => x !== d) : [...form.weekly_offs, d].sort(),
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      start_time: s.start_time,
      end_time: s.end_time,
      weekly_offs: s.weekly_offs || [],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editingId) {
        await api.patch(`/shifts/${editingId}`, form);
        toast.success("Shift updated");
      } else {
        await api.post("/shifts", form);
        toast.success("Shift created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete shift "${s.name}"? Employees on this shift will be unassigned.`)) return;
    try {
      await api.delete(`/shifts/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const fmtWeeklyOffs = (offs) => {
    if (!offs || offs.length === 0) return "None";
    return offs.map((d) => WEEKDAY_LABELS[d]).join(", ");
  };

  return (
    <section className="border border-gray-200 rounded-sm p-6 space-y-4" data-testid="shifts-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock size={18} weight="duotone" className="text-[#0047AB]" />
          <h2 className="font-display text-lg font-bold tracking-tight">Shifts</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-[#0047AB] hover:bg-[#0033A0] text-white rounded-sm h-9" data-testid="shift-add-button">
              <Plus size={14} weight="bold" className="mr-1" /> New shift
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">
                {editingId ? "Edit shift" : "Create shift"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  placeholder="e.g. General 10-7"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="shift-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Start time</Label>
                  <Input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    data-testid="shift-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End time</Label>
                  <Input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    data-testid="shift-end-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Weekly off days</Label>
                <div className="flex flex-wrap gap-2" data-testid="shift-weekly-offs">
                  {WEEKDAY_LABELS.map((w, i) => {
                    const active = form.weekly_offs.includes(i);
                    return (
                      <button
                        type="button"
                        key={i}
                        onClick={() => toggleDay(i)}
                        className={`px-3 h-8 border rounded-sm text-xs font-bold uppercase tracking-wider transition ${active ? "bg-[#0047AB] text-white border-[#0047AB]" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}
                        data-testid={`shift-day-${w.toLowerCase()}`}
                      >
                        {w}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-500">Selected days will be marked as Weekly-off on the attendance calendar.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} className="bg-[#0047AB] hover:bg-[#0033A0] text-white rounded-sm" data-testid="shift-save-button">
                {editingId ? "Save changes" : "Create shift"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-gray-100 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/60">
              <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-gray-500">Name</th>
              <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-gray-500">Timing</th>
              <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-gray-500">Weekly off</th>
              <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-gray-500"></th>
            </tr>
          </thead>
          <tbody data-testid="shifts-list">
            {shifts.length === 0 && (
              <tr><td colSpan="4" className="px-3 py-6 text-center text-gray-500 text-sm">No shifts yet. Create one and assign to employees.</td></tr>
            )}
            {shifts.map((s) => (
              <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{s.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{s.start_time} → {s.end_time}</td>
                <td className="px-3 py-2 text-xs">{fmtWeeklyOffs(s.weekly_offs)}</td>
                <td className="px-3 py-2 text-right space-x-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(s)} data-testid={`shift-edit-${s.name}`}>
                    <PencilSimple size={12} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-[#FF2400]" onClick={() => remove(s)} data-testid={`shift-delete-${s.name}`}>
                    <Trash size={12} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
